import React, { useEffect } from 'react'
import { useFetcher } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { Cookie } from 'lucide-react'
import { Switch } from '~/components/ui/switch.tsx'
import { Label } from '~/components/ui/label.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '~/components/ui/accordion.tsx'

export interface ConsentData {
	analytics?: boolean | undefined
	marketing?: boolean | undefined
}

interface CookieConsentContextProps {
	analytics?: boolean | undefined
	setAnalytics: (enabled: boolean) => void
	marketing?: boolean | undefined
	setMarketing: (enabled: boolean) => void
}

export const CookieConsentContext =
	React.createContext<CookieConsentContextProps>(
		{} as CookieConsentContextProps,
	)

export const CookieConsentProvider = ({
	children,
	consentData,
}: {
	children: React.ReactNode
	consentData?: ConsentData | undefined
}) => {
	const [analytics, setAnalytics] = React.useState(true)
	const [marketing, setMarketing] = React.useState(true)

	return (
		<CookieConsentContext.Provider
			value={{
				analytics,
				setAnalytics,
				marketing,
				setMarketing,
			}}
		>
			{children}
		</CookieConsentContext.Provider>
	)
}

export const CookieConsentBanner = () => {
	const id = 'cookie-consent'
	const formId = id + '-form'
	const { t } = useTranslation()
	const { analytics, marketing, setAnalytics, setMarketing } =
		React.useContext(CookieConsentContext)
	const acceptFormRef = React.useRef<HTMLFormElement>(null)
	const fetcher = useFetcher()

	const acceptOnKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape' && acceptFormRef.current) {
			event.preventDefault()
			acceptFormRef.current.requestSubmit()
		}
	}

	useEffect(() => {
		//handle the escape key for keyboard navigation
		window.addEventListener('keydown', acceptOnKeyDown)

		return () => {
			window.removeEventListener('keydown', acceptOnKeyDown)
		}
	})

	if (fetcher.state !== 'idle') {
		return null
	}

	return (
		<div
			className="fixed bottom-0 z-50 w-full bg-white/80 p-4 shadow-md ring-1 ring-gray-900/10 md:bg-white/95 md:p-6"
			role={'dialog'}
			aria-describedby={id + '-text'}
			aria-labelledby={id + '-header'}
		>
			<div className="container mx-auto">
				<div className="grid grid-cols-1 gap-2">
					<div
						id={id + '-header'}
						className="mb-1 flex items-center space-x-2 text-lg font-semibold text-gray-900 md:mb-2"
					>
						<Cookie className="h-5 w-5" />
						<span>{t('cookieConsent.title')}</span>
					</div>
					<div
						id={id + '-text'}
						className="mb-1 text-xs text-gray-800 md:mb-2 md:text-sm"
					>
						{t('cookieConsent.disclaimer')}
					</div>

					<form
						action="/settings/cookie-consent"
						replace
						method={'post'}
						id={formId + '-accept'}
					>
						<Accordion
							type="single"
							collapsible
							id={id + '-accordion'}
							className="w-full md:max-w-md"
						>
							<AccordionItem
								id={id + '-accordion-item'}
								value="settings-accordion-value"
							>
								<AccordionTrigger>Settings</AccordionTrigger>
								<AccordionContent>
									<div className="mb-4 flex flex-wrap items-center gap-2">
										<div className="flex items-center space-x-2">
											<Label htmlFor={id + '-necessary'}>
												{t('cookieConsent.label.necessary')}
											</Label>
											<Switch
												id={id + '-necessary'}
												name={'necessary'}
												checked={true}
												disabled={true}
												className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-indigo-600 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
											/>
										</div>
										<div className="flex items-center space-x-2">
											<Label htmlFor={id + '-analytics'}>
												{t('cookieConsent.label.analytics')}
											</Label>
											<Switch
												id={id + '-analytics'}
												name={'analytics'}
												checked={analytics ?? true}
												onChange={setAnalytics}
												aria-label={t('cookieConsent.label.analytics')}
												aria-checked={analytics ?? true}
											/>
										</div>
										<div className="flex items-center space-x-2">
											<Label htmlFor={id + '-marketing'}>
												{t('cookieConsent.label.marketing')}
											</Label>
											<Switch
												id={id + '-marketing'}
												name={'marketing'}
												checked={marketing ?? true}
												onChange={setMarketing}
												aria-label={t('cookieConsent.label.marketing')}
												aria-checked={marketing ?? true}
											/>
										</div>
										<input
											type="hidden"
											name="analytics"
											value={String(analytics ?? true)}
										/>
										<input
											type="hidden"
											name="marketing"
											value={String(marketing ?? true)}
										/>
									</div>
								</AccordionContent>
							</AccordionItem>
						</Accordion>
						<div className="mt-2 flex items-center justify-end gap-4">
							<button
								form={formId + '-deny'}
								type={'submit'}
								tabIndex={3}
								className="text-gray-600 hover:text-gray-900"
							>
								{t('cookieConsent.deny')}
							</button>
							<button
								form={formId + '-accept'}
								className="rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
								type={'submit'}
								tabIndex={2}
							>
								{t('cookieConsent.accept')}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	)
}
